import React, { useState } from 'react'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { DotsVerticalIcon } from 'src/editor/shared/icons/dots-vertical-icon'
import { Settings02Icon } from 'src/editor/shared/icons/settings-02-icon'
import { ActionMenu } from 'src/editor/shared/ui-lib/action-menu'
import {
  ButtonSizeEnum,
  ButtonTypeEnum,
  CommonButton,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import {
  BlogCategoryMockItem,
  blogCategories,
} from 'src/shared/mocks/website-blog-categories.mock'
import { BlogCategoryActionItemEnum } from './enums'
import { BlogCategoryFormValues } from './types'

interface CategoriesListingProps {
  onEditCategory: (categoryValues: BlogCategoryFormValues) => void
  onOpenCategory: (category: BlogCategoryMockItem) => void
}

export const CategoriesListing = ({
  onEditCategory,
  onOpenCategory,
}: CategoriesListingProps) => {
  const { t } = useTypedTranslation()
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const isAllSelected =
    blogCategories.length > 0 &&
    blogCategories.every(category => selectedIds.has(category.id))

  const handleToggleCategory = (id: number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    setSelectedIds(new Set(blogCategories.map(category => category.id)))
  }

  const handleCategoryActionSelect = (
    action: BlogCategoryActionItemEnum,
    category: BlogCategoryMockItem,
  ) => {
    if (action === BlogCategoryActionItemEnum.Settings) {
      onEditCategory({
        name: category.name,
        path: category.path,
        description: category.description ?? '',
        seoTitle: category.seoTitle ?? '',
        seoDescription: category.seoDescription ?? '',
      })
    } else if (action === BlogCategoryActionItemEnum.Delete) {
      // TODO: open delete category modal.
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {blogCategories.map(category => (
          <div
            key={category.id}
            className="flex items-center gap-4 rounded-md px-3 py-2 hover:bg-bgSecondary"
          >
            <CheckboxNew
              checked={selectedIds.has(category.id)}
              onChange={checked => handleToggleCategory(category.id, checked)}
            />
            <button
              type="button"
              className="flex min-w-0 flex-1 flex-col gap-1.5 text-left"
              onClick={() => onOpenCategory(category)}
            >
              <span className="truncate text-M text-textPrimary">
                {category.name}
              </span>
              <span className="text-S text-textTertiary">
                {category.postsCount}{' '}
                {t('website_settings.sidebar.blog.content.posts')}
              </span>
            </button>
            <ActionMenu
              items={[
                {
                  id: BlogCategoryActionItemEnum.Settings,
                  label: t(
                    'website_settings.sidebar.blog.categories.actions.settings',
                  ),
                  iconBefore: () => <Settings02Icon className="h-5 w-5" />,
                },
              ]}
              onSelect={action =>
                handleCategoryActionSelect(
                  action as BlogCategoryActionItemEnum,
                  category,
                )
              }
              triggerElement={setIsOpened => (
                <GhostIconButton
                  onClick={() => setIsOpened(true)}
                  icon={<DotsVerticalIcon className="fill-iconSecondary" />}
                  title={t(
                    'website_settings.sidebar.blog.categories.actions.settings',
                  )}
                />
              )}
            />
          </div>
        ))}
      </div>
      {selectedIds.size > 0 && (
        <div className="flex gap-2.5 border-border200 border-t pt-2">
          {!isAllSelected && (
            <CommonButton
              type={ButtonTypeEnum.Ghost}
              size={ButtonSizeEnum.Medium}
              className="flex-1"
              iconBefore={<CheckSquareIcon className="h-5 w-5" />}
              onClick={handleSelectAll}
            >
              {t('website_settings.sidebar.blog.categories.select_all')}
            </CommonButton>
          )}
        </div>
      )}
    </div>
  )
}
